import {
    ScanSearch,
    SmilePlus,
    Ruler,
    Timer,
    ShieldCheck,
    Eye,
} from 'lucide-react';

import { motion } from 'framer-motion';

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.15,
            duration: 0.6,
            ease: 'easeOut',
        },
    }),
};

export default function TechnologyImportance() {
    const features = [
        {
            icon: <ScanSearch className="w-8 h-8 text-teal-500 mb-4" />,
            title: 'Faster Diagnoses',
            desc: 'Digital imaging and 3D scans provide instant, high-definition visuals so we can detect problems early—before they become painful or costly.',
        },
        {
            icon: <SmilePlus className="w-8 h-8 text-lime-500 mb-4" />,
            title: 'More Comfortable Care',
            desc: 'Laser dentistry, digital impressions, and no-drill options reduce discomfort, eliminate mess, and make your visit smooth and stress-free.',
        },
        {
            icon: <Ruler className="w-8 h-8 text-teal-500 mb-4" />,
            title: 'Precise Results',
            desc: 'Advanced tools allow for pinpoint accuracy in restorative and cosmetic procedures—leading to more natural-looking and longer-lasting results.',
        },
        {
            icon: <Timer className="w-8 h-8 text-lime-500 mb-4" />,
            title: 'Less Time in the Chair',
            desc: 'With same-day crowns, faster imaging, and streamlined procedures, you\'ll spend less time at the dentist and more time enjoying life.',
        },
        {
            icon: <ShieldCheck className="w-8 h-8 text-teal-500 mb-4" />,
            title: 'Better Prevention',
            desc: 'Early detection with digital scans means we can prevent dental issues before they start, saving you from more complex treatments later.',
        },
        {
            icon: <Eye className="w-8 h-8 text-lime-500 mb-4" />,
            title: 'Patient Empowerment',
            desc: 'Intraoral cameras let you see exactly what we see, so you understand your diagnosis and can make informed decisions about your care.',
        },
    ];

    return (
        <section className="bg-gradient-to-br from-teal-50 via-white to-lime-50 py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto text-center space-y-6">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800">
                    Why Dental Technology Matters
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    Modern dental technology doesn't just make treatments faster—it makes them better. From earlier detection of issues to more precise and comfortable procedures, today's tools are designed to enhance your overall experience and long-term oral health.
                </p>
            </div>

            <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {features.map((feature, i) => (
                    <motion.div
                        key={i}
                        className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition"
                        custom={i}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={cardVariants}
                    >
                        {feature.icon}
                        <h3 className="text-xl font-semibold text-teal-600">{feature.title}</h3>
                        <p className="text-gray-600 mt-2">{feature.desc}</p>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}